import { forwardRef, useEffect, useRef } from "react";
import { Button } from "../button";
import { Textarea } from "../textarea";

interface InlineTextareaControlProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: string;
  icon?: React.ReactNode;
}

export const InlineTextareaControl = forwardRef<HTMLTextAreaElement, InlineTextareaControlProps>(
  ({ value, onChange, onSave, onCancel, placeholder, disabled, autoFocus = true, error, icon }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    useEffect(() => {
      if (autoFocus) {
        const timer = setTimeout(() => {
          textareaRef.current?.focus();
          textareaRef.current?.select();
        }, 50);
        return () => clearTimeout(timer);
      }
    }, [autoFocus, textareaRef]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSave();
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          {icon && <span className="text-muted-foreground shrink-0 mt-2">{icon}</span>}
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={!!error}
            rows={3}
            className="resize-none text-sm flex-1"
          />
        </div>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={disabled}>
            Cancel
          </Button>
          <Button size="sm" onClick={onSave} disabled={disabled}>
            {disabled ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    );
  },
);
InlineTextareaControl.displayName = "InlineTextareaControl";
